'use client';

import React, { forwardRef } from 'react';
import classnames from 'classnames';

import Box from '../Box/Box';
import Checkbox from '../Checkbox/Checkbox';
import Typography from '../Typography/Typography';

import styles from './CheckboxGroup.module.scss';

type Theme = 'dark' | 'light';
type Direction = 'ltr' | 'rtl';
type ValidationType = 'success' | 'warning' | 'error';

type ValidationResponse = {
  validationType: ValidationType;
  message: string;
};

type CheckboxOption = {
  value: string;
  label: string;
  description?: string;
  default?: boolean;
  disabled?: boolean;
};

type RegisterFunction = {
  (name: string): {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
    name: string;
    ref: React.Ref<HTMLInputElement>;
  };
};

export type CheckboxGroupProps = {
  /** Unique name for the checkbox group (used for form registration) */
  name: string;
  /** Optional register function from react-hook-form */
  register?: RegisterFunction;
  /** Theme variant */
  theme?: Theme;
  /** Label text displayed above the checkbox group */
  label?: string;
  /** Helper text displayed below the label */
  helperText?: string;
  /** Array of checkbox options */
  options: CheckboxOption[];
  /** Currently selected values */
  selectedValues?: string[];
  /** Text direction */
  direction?: Direction;
  /** Whether the entire group is disabled */
  isDisabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Callback when selection changes */
  handleChange?: (selectedValues: string[]) => void;
  /** Test ID for the component */
  dataTestId: string;
  /** Google Analytics component name */
  gaComponent?: string;
  /** Whether to log events to analytics */
  logEvents?: boolean;
  /**
   * Validation response object containing error/warning/success state and message.
   */
  validationResponse?: ValidationResponse;
  /**
   * Whether to show the validation message area.
   * @default true
   */
  showValidation?: boolean;
  /** Optional className for additional styling */
  className?: string;
  /** Layout direction for the checkboxes */
  layout?: 'vertical' | 'horizontal';
};

/**
 * CheckboxGroup component for managing multiple checkbox selections.
 *
 * @component
 * @param {CheckboxGroupProps} props - Component props
 * @returns {JSX.Element} CheckboxGroup component
 * @example
 * <CheckboxGroup
 *   name="preferences"
 *   options={[
 *     { value: 'email', label: 'Email notifications' },
 *     { value: 'sms', label: 'SMS notifications' },
 *   ]}
 *   selectedValues={['email']}
 *   handleChange={(values) => console.log(values)}
 *   dataTestId="preferences-group"
 * />
 */
const CheckboxGroup = forwardRef<HTMLDivElement, CheckboxGroupProps>(
  (
    {
      name,
      register,
      theme = 'dark',
      label,
      helperText,
      options,
      selectedValues = [],
      direction = 'ltr',
      isDisabled = false,
      required = false,
      handleChange = () => {},
      dataTestId,
      gaComponent = '',
      logEvents = true,
      validationResponse,
      showValidation = true,
      className,
      layout = 'vertical',
    },
    ref
  ) => {
    const handleCheckboxChange = (value: string, checked: boolean) => {
      let newSelectedValues: string[];

      if (checked) {
        newSelectedValues = [...selectedValues, value];
      } else {
        newSelectedValues = selectedValues.filter((v) => v !== value);
      }

      handleChange(newSelectedValues);
    };

    return (
      <Box
        ref={ref}
        className={classnames(
          styles.checkboxGroup,
          styles[`theme_${theme}`],
          styles[`validation-${validationResponse?.validationType}`],
          className
        )}
      >
        {/* Hidden input for react-hook-form registration */}
        {typeof register === 'function' && (
          <input type="hidden" {...register(name)} value={selectedValues.join(',')} />
        )}
        {(label || helperText) && (
          <Box mb={8}>
            {label && (
              <Typography
                className={styles.label}
                variant="label"
                size="small"
                direction={direction}
                theme={theme}
                color="gray-3"
              >
                {label}
                {required && '*'}
              </Typography>
            )}
            {helperText && (
              <Typography
                className={styles.helperText}
                variant="body"
                size="extraSmall"
                direction={direction}
                theme={theme}
                color="gray-5"
              >
                {helperText}
              </Typography>
            )}
          </Box>
        )}
        <ul
          className={classnames(styles.checkboxList, styles[layout])}
          role="group"
          aria-label={label || name}
          data-testid={dataTestId}
        >
          {options.map((option, index) => {
            const isChecked = selectedValues.includes(option.value) || option.default;
            const optionDisabled = isDisabled || option.disabled;

            return (
              <li key={option.value} className={styles.checkboxItem}>
                <div className={styles.checkboxWrapper}>
                  <Checkbox
                    name={`${name}.${option.value}`}
                    theme={theme}
                    label={option.label}
                    direction={direction}
                    isChecked={isChecked}
                    isDisabled={optionDisabled}
                    handleChange={(checked) => handleCheckboxChange(option.value, checked)}
                    dataTestId={`${dataTestId}-option-${index}`}
                    gaComponent={gaComponent}
                    logEvents={logEvents}
                    showValidation={false}
                  />
                  {option.description && (
                    <Typography
                      variant="body"
                      size="extraSmall"
                      className={classnames(styles.description, styles[`theme_${theme}`])}
                      direction={direction}
                    >
                      {option.description}
                    </Typography>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {showValidation && (
          <Box className={styles.validationMessageWrapper} role="alert" aria-live="assertive">
            {validationResponse?.message && (
              <Typography
                className={styles.validationMessage}
                variant="body"
                size="extraSmall"
                direction={direction}
                theme={theme}
                id={`${name}-validation-message`}
              >
                {validationResponse.message}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

CheckboxGroup.displayName = 'CheckboxGroup';

export default CheckboxGroup;
